import React, { useState, useEffect } from 'react';
import { Button, Input, Checkbox, Dropdown, Radio } from 'antd';
import { DownOutlined, SortAscendingOutlined, SortDescendingOutlined, SearchOutlined } from '@ant-design/icons';
import './ExcelFilter.css';

const ExcelFilter = ({ 
  options = [], 
  selectedValues = [], 
  onApply, 
  onSort,
  currentSortState = "default",
  placeholder = "Filter...",
  label = "Filter",
  singleSelect = false,
  showSort = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [tempSelectedValues, setTempSelectedValues] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);

  // Initialize temp values when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setTempSelectedValues([...selectedValues]);
      setSearchText('');
    }
  }, [isOpen, selectedValues]);

  // Filter options based on search text
  useEffect(() => {
    if (!searchText.trim()) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option => 
        option.label?.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [searchText, options]);

  // Handle single select click (for single select mode) - removed auto-apply
  // const handleSingleSelect = (value) => {
  //   if (singleSelect) {
  //     setTempSelectedValues([value]);
  //     // Removed auto-apply - now uses OK/Cancel buttons
  //   }
  // };

  // Handle individual checkbox/radio change
  const handleCheckboxChange = (value, checked) => {
    if (singleSelect) {
      // For single select, replace the selection
      setTempSelectedValues(checked ? [value] : []);
    } else {
      // For multi select, add/remove from array
      if (checked) {
        setTempSelectedValues(prev => [...prev, value]);
      } else {
        setTempSelectedValues(prev => prev.filter(v => v !== value));
      }
    }
  };

  // Handle radio button change for single select
  const handleRadioChange = (e) => {
    setTempSelectedValues([e.target.value]);
  };

  // Handle select all
  const handleSelectAll = (checked) => {
    if (singleSelect) {
      // For single select, don't allow select all
      return;
    }
    
    if (checked) {
      setTempSelectedValues(filteredOptions.map(opt => opt.value));
    } else {
      setTempSelectedValues([]);
    }
  };

  // Handle clear all
  const handleClear = () => {
    setTempSelectedValues([]);
  };

  // Handle reset (clear and apply)
  const handleReset = () => {
    onApply([]);
    onSort('default'); // Reset sorting to default
    setIsOpen(false);
  };

  // Handle apply filters
  const handleApply = () => {
    onApply(tempSelectedValues);
    setIsOpen(false);
  };

  // Handle cancel
  const handleCancel = () => {
    setTempSelectedValues([...selectedValues]);
    setIsOpen(false);
  };

  // Handle sort
  const handleSort = (direction) => {
    onSort(direction);
    setIsOpen(false);
  };

  // Check if all visible options are selected
  const isAllSelected = filteredOptions.length > 0 && 
    filteredOptions.every(option => tempSelectedValues.includes(option.value));

  // Check if some visible options are selected
  const isIndeterminate = filteredOptions.some(option => tempSelectedValues.includes(option.value)) && 
    !isAllSelected;

  const dropdownContent = (
    <div className="excel-filter-dropdown" style={{ width: '280px', padding: '8px' }}>
      {/* Sort Options */}
      {showSort && (
        <div className="sort-section" style={{ marginBottom: options.length > 0 ? '8px' : '0', borderBottom: options.length > 0 ? '1px solid #f0f0f0' : 'none', paddingBottom: options.length > 0 ? '8px' : '0' }}>
          <div 
            className="sort-option" 
            onClick={() => handleSort('ascending')}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              padding: '4px 8px', 
              cursor: 'pointer',
              backgroundColor: currentSortState === 'ascending' ? '#e6f7ff' : 'transparent',
              textAlign: 'left'
            }}
          >
            <SortAscendingOutlined style={{ marginRight: '8px' }} />
            Sort A to Z
          </div>
          <div 
            className="sort-option" 
            onClick={() => handleSort('descending')}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              padding: '4px 8px', 
              cursor: 'pointer',
              backgroundColor: currentSortState === 'descending' ? '#e6f7ff' : 'transparent',
              textAlign: 'left'
            }}
          >
            <SortDescendingOutlined style={{ marginRight: '8px' }} />
            Sort Z to A
          </div>
        </div>
      )}

      {/* Only show filter options if there are options available */}
      {options.length > 0 && (
        <>
          {/* Search */}
          <div style={{ marginBottom: '8px' }}>
            <Input
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
              size="small"
            />
          </div>

          {/* Select All / Clear */}
          {!singleSelect && (
            <div style={{ marginBottom: '8px', borderBottom: '1px solid #f0f0f0', paddingBottom: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                >
                  Select All ({filteredOptions.length})
                </Checkbox>
                <Button 
                  type="link" 
                  size="small" 
                  onClick={handleClear}
                  style={{ padding: '0', height: 'auto' }}
                >
                  Clear
                </Button>
              </div>
            </div>
          )}

          {/* Clear button for single select */}
          {singleSelect && (
            <div style={{ marginBottom: '8px', borderBottom: '1px solid #f0f0f0', paddingBottom: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '4px 0' }}>
                <Button 
                  type="link" 
                  size="small" 
                  onClick={handleClear}
                  style={{ padding: '0', height: 'auto' }}
                  disabled={tempSelectedValues.length === 0}
                >
                  Clear Selection
                </Button>
              </div>
            </div>
          )}

          {/* Filter Options */}
          <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '8px' }}>
            {!singleSelect ? (
              // Multi-select with checkboxes
              filteredOptions.map((option, index) => (
                <div key={`${option.value}-${index}`} style={{ padding: '2px 0', textAlign: 'left' }}>
                  <Checkbox
                    checked={tempSelectedValues.includes(option.value)}
                    onChange={(e) => handleCheckboxChange(option.value, e.target.checked)}
                  >
                    {option.label}
                  </Checkbox>
                </div>
              ))
            ) : (
              // Single-select with radio buttons
              <Radio.Group 
                value={tempSelectedValues[0] || null} 
                onChange={handleRadioChange}
                style={{ width: '100%' }}
              >
                {filteredOptions.map((option, index) => (
                  <div key={`${option.value}-${index}`} style={{ padding: '2px 0', textAlign: 'left' }}>
                    <Radio value={option.value}>
                      {option.label}
                    </Radio>
                  </div>
                ))}
              </Radio.Group>
            )}
            {filteredOptions.length === 0 && options.length > 0 && (
              <div style={{ textAlign: 'center', color: '#999', padding: '16px' }}>
                No items found
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {!singleSelect && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', borderTop: '1px solid #f0f0f0', paddingTop: '8px' }}>
              <Button size="small" type="link" onClick={handleReset}>
                Reset
              </Button>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button size="small" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button size="small" type="primary" onClick={handleApply}>
                  OK
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Action Buttons for single select */}
      {singleSelect && options.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', borderTop: '1px solid #f0f0f0', paddingTop: '8px' }}>
          <Button size="small" type="link" onClick={handleReset}>
            Reset
          </Button>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button size="small" onClick={handleCancel}>
              Cancel
            </Button>
            <Button size="small" type="primary" onClick={handleApply}>
              OK
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  // Count selected items for display
  const selectedCount = selectedValues.length;
  const totalCount = options.length;

  // Check if filter is active (Excel-style)
  const isFilterActive = selectedCount > 0 && !(selectedCount === totalCount);
  
  // Check if sort is active (not default)
  const isSortActive = currentSortState !== "default";
  
  // Button should be blue if either filter or sort is active
  const isActive = isFilterActive || isSortActive;

  return (
    <Dropdown
      open={isOpen}
      onOpenChange={setIsOpen}
      dropdownRender={(menu) => dropdownContent}
      trigger={['click']}
      placement="bottomRight"
      getPopupContainer={(trigger) => trigger.parentNode}
    >
      <Button 
        size="small"
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          minWidth: '20px',
          width: '20px',
          height: '20px',
          padding: '0',
          border: '1px solid #d9d9d9',
          backgroundColor: isActive ? '#1890ff' : 'white',
          borderColor: isActive ? '#1890ff' : '#d9d9d9',
          color: isActive ? 'white' : 'rgba(0, 0, 0, 0.65)'
        }}
      >
        <DownOutlined style={{ fontSize: '10px' }} />
      </Button>
    </Dropdown>
  );
};

export default ExcelFilter;